import { api } from "./api";
import { formatPacificDate } from "./format";

/**
 * Everything the Audit page does that is not JSX.
 *
 * The only page with no buttons. It answers three questions the rest of the app
 * cannot: which artists are being crawled and when each was last checked, what
 * the auto-filter has hidden, and what it has learned.
 */

export interface ArtistRow {
  artist: string;
  listings: number;
  last_checked: string;
}

export interface FilteredRow {
  key: string;
  artist: string;
  title: string;
  url: string;
  platform: string;
  matched_pattern: string;
  filtered_at: string;
}

export interface LearnedPattern {
  artist: string;
  keywords: string[];
  source_count: number;
  example_titles: string[];
}

export interface AuditPage {
  artists: ArtistRow[];
  filtered: FilteredRow[];
  patterns: LearnedPattern[];
  dismissed_count: number;
}

export async function fetchAudit(): Promise<AuditPage> {
  const res = await api.authedFetch("/api/audit");
  if (!res.ok) throw new Error(`Could not load the audit (HTTP ${res.status})`);
  return (await res.json()) as AuditPage;
}

/**
 * "Never" for an artist the scraper has not reached.
 *
 * Distinct from an unparseable date, which renders the raw string rather than
 * claiming "Never": an artist that HAS been checked but whose timestamp is
 * malformed is a different problem, and flattening the two would hide it.
 */
export function lastCheckedLabel(iso: string): string {
  if (!iso) return "Never";
  return formatPacificDate(iso) ?? iso;
}

/** Days since an artist was last crawled, or null when never or unparseable. */
export function daysSinceChecked(iso: string, now: Date): number | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return Math.floor((now.getTime() - at.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * How long since this artist was SEARCHED BY NAME, and what that means.
 *
 * `artists_checked` is written by the scraper in the site-checker repo and it
 * does not mean "when did we last look at this band". Three things about it:
 *
 * 1. It covers Depop and Poshmark only -- the two platforms searched per
 *    artist. Grailed, Shopify, Bandcamp, BigCartel and OpenCart are crawled a
 *    different way and never write here, so an artist whose merch all comes
 *    from a Bandcamp store shows "Never" while being crawled perfectly.
 * 2. The search pool is driven by Spotify top artists and rotates. Both
 *    platforms use a 20-hour TTL, so an artist IN the pool is searched daily.
 * 3. Entries LINGER for artists that have left the pool. The scraper's own
 *    source records 201 of 458 entries stale by up to 138 days.
 *
 * So an old date almost always means "this artist is no longer in the search
 * rotation", not "something is broken". The threshold below is the scraper's
 * own DEPOP_COVERAGE_STALE_DAYS rather than a number picked here.
 */
export const SEARCH_STALE_DAYS = 3;

/**
 * Searched by name recently enough to still be in the rotation.
 *
 * `days >= 0` is load-bearing and not defensive noise. The server coerces a
 * malformed `artists_checked` value rather than dropping it, so a value like
 * `12345` arrives as the string "12345" -- and `new Date("12345")` is NOT
 * Invalid Date, it is the YEAR 12345. That made `days` about -3.7 million,
 * which cleared `days < SEARCH_STALE_DAYS` and reported an artist with a
 * garbage timestamp as "In the search rotation" -- the exact inverse of what
 * this page exists to show. You cannot have been checked in the future.
 */
export function isSearchedRecently(iso: string, now: Date): boolean {
  const days = daysSinceChecked(iso, now);
  return days !== null && days >= 0 && days < SEARCH_STALE_DAYS;
}

/**
 * What to say about an artist's search coverage.
 *
 * Three states rather than a boolean, because "never searched" and "searched
 * six months ago" have different explanations and collapsing them into one
 * amber row would tell you nothing about which you were looking at.
 */
export type SearchState = "current" | "dropped" | "never";

export function searchState(iso: string, now: Date): SearchState {
  if (!iso) return "never";
  return isSearchedRecently(iso, now) ? "current" : "dropped";
}

export function searchStateLabel(state: SearchState): string {
  if (state === "current") return "In the search rotation";
  if (state === "dropped") return "Not in the current search rotation";
  return "Never searched by name (found via a retail crawl)";
}

/** `160 artists · 1,971 listings tracked`. */
export function artistsSummary(page: AuditPage): string {
  const total = page.artists.reduce((sum, a) => sum + a.listings, 0);
  return `${page.artists.length} artist${page.artists.length === 1 ? "" : "s"} · ${total} visible listing${total === 1 ? "" : "s"}`;
}
